// Icons
import Error from '@mui/icons-material/Error'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Close'

// Material-UI components
import { Checkbox, Stack, Tooltip, Typography } from '@mui/material'

// Data Grid
import { GridRowModes } from '@mui/x-data-grid-premium'

// API and constants
import { API_URL, METHODS } from '../../api/constants'
import { request } from '../../api/axios'
import {
  CONTENT,
  PAGE_TITLE
} from '../../constants/constants'

// Hooks
import { useAxiosPrivate, useDocumentTitle } from '../../hooks'

// Utilities
import { mask } from '../../utils'

// Custom components
import {
  CustomEditInputCell,
  GridActionCellItem,
} from '../../components/common/kc-table/components'
import { EncryptedCell } from '../../components/common/EncryptedCell'
import GlobalConfigList from '../../components/GlobalConfigList/GlobalConfigList'
import { Warning } from '@mui/icons-material'

function renderEditCell(params) {
  return <CustomEditInputCell {...params} />
}

const getColumns = ({
  onSave,
  onEdit,
  onCancel,
  onDelete,
  getMode,
  apiRef,
}) => {
  const columns = [
    {
      field: 'created_at',
      hide: true,
    },
    {
      field: 'key',
      headerName: 'Key *',
      minWidth: 300,
      flex: 1,
      editable: true,
      renderCell: (params) => {
        const portConfigError =
          params.row?.portConfigError?.monitor ||
          params.row?.portConfigError?.tcp
        return (
          <>
            <Typography
              variant="body2"
              sx={{
                fontWeight: params?.row?.type === 'port' ? '700' : 'normal',
              }}
            >
              {params?.value}
            </Typography>
            {params?.row?.type === 'port' && portConfigError && (
              <Tooltip title={portConfigError} arrow>
                <Warning color="warning" fontSize="small" sx={{ ml: 0.5 }} />
              </Tooltip>
            )}
          </>
        )
      },
      renderEditCell: renderEditCell,
    },
    {
      field: 'value',
      headerName: 'Value *',
      minWidth: 400,
      flex: 1,
      editable: true,
      cellClassName: (params) => {
        return params?.row?.encrypted ? 'data-grid-cell-encrypted' : null
      },
      valueGetter: (params) => {
        const isEncrypted = params.row?.encrypted
        return isEncrypted ? mask(params?.value) : params?.value
      },
      renderEditCell: renderEditCell,
    },
    {
      field: 'encrypted',
      headerName: 'Encrypted',
      minWidth: 150,
      flex: 0.5,
      editable: true,
      type: 'boolean',
      renderEditCell: (params) => {
        const { id, value, field } = params
        return (
          <Checkbox
            checked={Boolean(value)}
            onChange={(event) =>
              apiRef.current.setEditCellValue({
                id,
                field,
                value: event.target.checked,
              })
            }
          />
        )
      },
      // custom
      renderCell: (params) => {
        const isEncrypted = params.value
        return (
          <Stack alignItems="center">
            <EncryptedCell encrypted={isEncrypted} />
            {params.row?.note && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ ml: 0.5 }}
              >
                {params.row?.note}
              </Typography>
            )}
          </Stack>
        )
      },
    },

    {
      field: 'comment',
      headerName: 'Comment',
      minWidth: 350,
      editable: true,
      flex: 1,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        const isEncrypted = row?.encrypted
        const mode = getMode(id)
        const isInEditMode = mode === GridRowModes.Edit
        const isPortConfig = row?.key?.toLowerCase()?.startsWith('port-')
        // Prevent editing of port configurations
        if (isPortConfig) {
          return []
        }

        if (isInEditMode) {
          return [
            <GridActionCellItem
              icon={<SaveIcon fontSize="2.5rem" />}
              label="Save"
              onClick={onSave(id)}
              disabled={isEncrypted}
            />,
            <GridActionCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={onCancel(id)}
              color="inherit"
            />,
          ]
        }

        return [
          <GridActionCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={onEdit(id)}
            color="inherit"
            title={
              isEncrypted
                ? 'Encrypted configs are not editable. Please, delete and add a new one instead.'
                : ''
            }
            disabled={isEncrypted}
          />,
          <GridActionCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={onDelete(id)}
            color="inherit"
          />,
        ]
      },
    },
  ]
  return columns
}
const preparePayload = (row) => {
  const { id, isNew, ...rest } = row
  return isNew ? rest : { ...rest, id }
}

const GlobalConfig = () => {
  useDocumentTitle(PAGE_TITLE.GLOBAL_CONFIG)
  const axiosPrivate = useAxiosPrivate()

  const handleUpdate = (newRow) => {
    const { id, ...payload } = newRow
    const path = id ? `${API_URL.GLOBAL_CONFIG}/${id}` : API_URL.GLOBAL_CONFIG
    const method = id ? METHODS.PUT : METHODS.POST
    const data = id ? { ...payload, id } : [payload]
    try {
      return request({
        axiosInstance: axiosPrivate,
        path,
        method,
        data,
      })
    } catch (error) {
      throw error
    }
  }

  const handleSave = async (newRow) => {
    const payload = preparePayload(newRow)
    const { id } = payload
    if (newRow.key?.trim() === '' || newRow.value?.trim() === '') {
      throw new Error(CONTENT.NO_EMPTY_KEY_VALUE)
    } else {
      try {
        const response = await handleUpdate(payload)
        return {
          status: response.status,
          message: id
            ? CONTENT.global_configs.success.update
            : CONTENT.global_configs.success.create,
        }
      } catch (error) {
        throw new Error(error?.response?.data?.errorMsg)
      }
    }
  }

  const handleDelete = async (id) => {
    try {
      const response = await request({
        axiosInstance: axiosPrivate,
        path: `${API_URL.GLOBAL_CONFIG}/${id}`,
        method: METHODS.DELETE,
        data: id,
      })
      return {
        status: response.status,
        message: CONTENT.global_configs.success.delete,
      }
    } catch (error) {
      throw new Error(CONTENT.UNKNOWN_ERROR)
    }
  }

  return (
    <GlobalConfigList
      getColumns={getColumns}
      onSave={handleSave}
      onDelete={handleDelete}
      maxHeight="calc(100% - 1rem)"
      initialState={{
        sorting: {
          sortModel: [
            {
              field: 'created_at',
              sort: 'asc',
            },
          ],
        },
      }}
    />
  )
}

export default GlobalConfig
